import React, { useEffect, useRef, useState } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { useThemeMode } from '../../../../_metronic/partials'
import { getCSSVariableValue } from '../../../../_metronic/assets/ts/_utils'
import { HttpService } from '../../../services/http.services'

type Props = {
  className: string
  chartColor: string
  chartHeight: string
}

type ChartType = 'bar' | 'line'

const NotificationBarChart: React.FC<Props> = ({ className, chartColor, chartHeight }) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartInstance = useRef<ApexCharts | null>(null)
  const { mode } = useThemeMode()

  const [showOutOfThreshold, setShowOutOfThreshold] = useState(true)
  const [showInThreshold, setShowInThreshold] = useState(true)
  const [outOfThresholdChartType, setOutOfThresholdChartType] = useState<ChartType>('bar')
  const [inThresholdChartType, setInThresholdChartType] = useState<ChartType>('bar')
  const [chartData, setChartData] = useState<{
    dates: string[]
    'Out of threshold': number[]
    'Offline': number[]
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch chart data
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true)
        const response = await HttpService.get('auth/dashboard/notification/chart/data')
        setChartData(response.data.results)
        setError(null)
      } catch (err: any) {
        console.error('Failed to fetch notification chart data:', err)
        const errorMessage = err?.data?.message || err?.message || 'Failed to load notification chart data'
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    fetchChartData()
  }, [])

  const refreshChart = () => {
    if (!chartRef.current || !chartData) return

    const options = chartOptions(
      chartColor,
      chartHeight,
      showOutOfThreshold,
      showInThreshold,
      outOfThresholdChartType,
      inThresholdChartType,
      chartData.dates || [],
      chartData['Out of threshold'] || [],
      chartData['Offline'] || []
    )

    if (chartInstance.current) {
      chartInstance.current.updateOptions(options)
    } else {
      const chart = new ApexCharts(chartRef.current, options)
      chart.render()
      chartInstance.current = chart
    }
  }

  useEffect(() => {
    refreshChart()
    return () => {
      chartInstance.current?.destroy()
      chartInstance.current = null
    }
  }, [chartRef, mode, chartData])

  useEffect(() => {
    refreshChart()
  }, [showOutOfThreshold, showInThreshold, outOfThresholdChartType, inThresholdChartType])

  if (loading) {
    return (
      <div className={`card ${className}`}>
        <div className="card-body d-flex justify-content-center align-items-center">
          <span className="spinner-border spinner-border-sm align-middle me-2"></span>
          Loading chart data...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`card ${className}`}>
        <div className="card-body d-flex justify-content-center align-items-center text-danger">
          {error}
        </div>
      </div>
    )
  }

  if (!chartData) {
    return (
      <div className={`card ${className}`}>
        <div className="card-body d-flex justify-content-center align-items-center">
          No chart data available
        </div>
      </div>
    )
  }

  // Get date range for subtitle
  const dateRange = chartData.dates && chartData.dates.length > 0
    ? `${chartData.dates[0]} - ${chartData.dates[chartData.dates.length - 1]}`
    : 'No date range available'

  return (
    <div className={`card ${className}`}>
      <div className='card-body p-0 d-flex justify-content-between flex-column overflow-hidden'>
        <div className='d-flex flex-stack flex-wrap flex-grow-1 px-9 pt-9 pb-3'>
          <div className='me-2'>
            <span className='fw-bold text-gray-800 d-block fs-3'>Alerts</span>
            <span className='text-gray-400 fw-semibold'>{dateRange}</span>
          </div>
        </div>

        <div ref={chartRef} className='mixed-widget-10-chart'></div>

        {/* Visibility Controls */}
        <div className='px-9 py-4'>
          <div className='d-flex justify-content-center align-items-center gap-5 mb-4'>
            <div className='d-flex align-items-center gap-5'>
              <label className='form-check form-check-sm form-check-custom form-check-solid m-0'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={showOutOfThreshold}
                  onChange={() => setShowOutOfThreshold(!showOutOfThreshold)}
                  style={{
                    backgroundColor: showOutOfThreshold ? getCSSVariableValue('--bs-danger') : '',
                    borderColor: showOutOfThreshold ? getCSSVariableValue('--bs-danger') : ''
                  }}
                />
                <span className='form-check-label text-dark fw-semibold'>Out of threshold &nbsp;</span>
                {showOutOfThreshold && (
                  <select
                    className='form-select form-select-sm ms-2'
                    style={{ width: 'auto', minWidth: '80px' }}
                    value={outOfThresholdChartType}
                    onChange={(e) => setOutOfThresholdChartType(e.target.value as ChartType)}
                  >
                    <option value='bar'>Bar</option>
                    <option value='line'>Line</option>
                  </select>
                )}
              </label>

              <label className='form-check form-check-sm form-check-custom form-check-solid m-0'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={showInThreshold}
                  onChange={() => setShowInThreshold(!showInThreshold)}
                  style={{
                    backgroundColor: showInThreshold ? (getCSSVariableValue('--bs-info') || '#17a2b8') : '',
                    borderColor: showInThreshold ? (getCSSVariableValue('--bs-info') || '#17a2b8') : ''
                  }}
                />
                <span className='form-check-label text-dark fw-semibold'>Offline &nbsp;</span>
                {showInThreshold && (
                  <select
                    className='form-select form-select-sm ms-2'
                    style={{ width: 'auto', minWidth: '80px' }}
                    value={inThresholdChartType}
                    onChange={(e) => setInThresholdChartType(e.target.value as ChartType)}
                  >
                    <option value='bar'>Bar</option>
                    <option value='line'>Line</option>
                  </select>
                )}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const chartOptions = (
  chartColor: string,
  chartHeight: string,
  showOut: boolean,
  showIn: boolean,
  outChartType: ChartType,
  inChartType: ChartType,
  dates: string[],
  outData: number[],
  offlineData: number[]
): ApexOptions => {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const offlineColor = getCSSVariableValue('--bs-gray') || getCSSVariableValue('--bs-primary') || '#17a2b8'
  const baseColor = getCSSVariableValue('--bs-' + chartColor)

  const series = []
  if (showOut) {
    series.push({
      name: 'Out of threshold',
      type: outChartType,
      data: outData,
    })
  }
  if (showIn) {
    series.push({
      name: 'Offline',
      type: inChartType,
      data: offlineData,
    })
  }

  const colors = []
  if (showOut) colors.push(baseColor)
  if (showIn) colors.push(offlineColor)

  const mainChartType = showOut && showIn
    ? (outChartType === inChartType ? outChartType : 'line')
    : (showOut ? outChartType : inChartType)

  return {
    series,
    chart: {
      fontFamily: 'inherit',
      type: mainChartType,
      height: chartHeight,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
        borderRadius: 5,
      },
    },
    stroke: {
      show: true,
      width: mainChartType === 'line' || (showOut && showIn && outChartType !== inChartType) ? [
        ...(showOut && outChartType === 'line' ? [4] : []),
        ...(showOut && outChartType === 'bar' ? [0] : []),
        ...(showIn && inChartType === 'line' ? [4] : []),
        ...(showIn && inChartType === 'bar' ? [0] : [])
      ] : 2,
      colors: mainChartType === 'line' || (showOut && showIn && outChartType !== inChartType) ? undefined : ['transparent'],
      curve: 'smooth',
    },
    markers: {
      size: mainChartType === 'line' || (showOut && showIn && (outChartType === 'line' || inChartType === 'line')) ? [
        ...(showOut && outChartType === 'line' ? [6] : []),
        ...(showOut && outChartType === 'bar' ? [0] : []),
        ...(showIn && inChartType === 'line' ? [6] : []),
        ...(showIn && inChartType === 'bar' ? [0] : [])
      ] : 0,
      hover: {
        sizeOffset: 2,
      }
    },
    legend: { show: false },
    dataLabels: { enabled: false },
    xaxis: {
      categories: dates,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    fill: {
      type: 'solid',
      opacity: mainChartType === 'line' || (showOut && showIn && (outChartType === 'line' || inChartType === 'line')) ? [
        ...(showOut && outChartType === 'line' ? [1] : []),
        ...(showOut && outChartType === 'bar' ? [1] : []),
        ...(showIn && inChartType === 'line' ? [1] : []),
        ...(showIn && inChartType === 'bar' ? [1] : [])
      ] : 1,
    },
    states: {
      normal: { filter: { type: 'none', value: 0 } },
      hover: { filter: { type: 'none', value: 0 } },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: { type: 'none', value: 0 },
      },
    },
    tooltip: {
      style: { fontSize: '12px' },
      y: {
        formatter: function (val) {
          return val + ''
        },
      },
    },
    colors,
    grid: {
      padding: { top: 10 },
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: { show: true },
      },
    },
  }
}

export { NotificationBarChart }